import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './NotificationBell.css';
import { useAuth } from '../../context/AuthContext';
import { formatRelativeTime, formatAbsoluteTime } from '../../utils/time';
import notificationsApi from '../../services/notifications';

const NotificationBell = () => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);

  useEffect(() => {
    if (!user?.username) return;
    let es = null;

    const load = async (showLoading = false) => {
      try {
        if (showLoading) {
          setIsInitialLoading(true);
        }

        const [notificationsData, count] = await Promise.all([
          notificationsApi.get(user.username, { limit: 50 }),
          notificationsApi.unreadCount(user.username),
        ]);

        setNotifications(notificationsData || []);
        setUnreadCount(Number(count) || 0);
      } catch (err) {
        console.error('load notifications', err);
      } finally {
        if (showLoading) {
          setIsInitialLoading(false);
        }
      }
    };

    load(true);

    const handleNotificationsChanged = () => {
      load();
    };

    const handleUnreadCountChanged = (event) => {
      const nextUnreadCount = Number(event?.detail?.unreadCount);
      if (Number.isFinite(nextUnreadCount) && nextUnreadCount >= 0) {
        setUnreadCount(nextUnreadCount);
        return;
      }

      load();
    };

    window.addEventListener('notifications:changed', handleNotificationsChanged);
    window.addEventListener('notifications:unread-count-changed', handleUnreadCountChanged);

    // SSE
    try {
      es = new EventSource(`/api/notifications/stream/${encodeURIComponent(user.username)}`);
      es.addEventListener('notification', (e) => {
        try {
          const parsed = JSON.parse(e.data);
          setNotifications(prev => [...parsed, ...prev]);
          setUnreadCount((c) => c + (parsed.length || 0));
        } catch (err) {}
      });
      es.onerror = (err) => {
        // reconnecting is handled server-side polling
      };
    } catch (err) {
      // ignore
    }

    return () => {
      try { if (es) es.close(); } catch (err) {}
      window.removeEventListener('notifications:changed', handleNotificationsChanged);
      window.removeEventListener('notifications:unread-count-changed', handleUnreadCountChanged);
    };
  }, [user?.username]);

  const toggleOpen = () => setOpen(o => !o);

  return (
    <div className="notification-bell">
      <button className="bell-btn" onClick={toggleOpen} title="Notifications">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={unreadCount > 0 ? 'has-unread' : ''}>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {isInitialLoading ? (
          <span className="badge badge-loading" aria-label="Loading notifications" title="Loading notifications">
            <span className="badge-spinner" aria-hidden="true" />
          </span>
        ) : unreadCount > 0 && <span className="badge">{unreadCount}</span>}
      </button>
      {open && (
        <div className="notif-dropdown">
          <div className="notif-header">
            <span>Notifications</span>
            <Link to="/notifications" className="notif-header-link" onClick={() => setOpen(false)}>
              View all
            </Link>
          </div>
          {notifications.length === 0 && <div className="notif-empty">No notifications</div>}
          <ul className="notif-list">
            {notifications.map((n, i) => (
              <li key={`${n.id || i}`} className={`notif-item ${n.is_read ? 'read' : 'unread'}`}>
                <div className="notif-time" title={formatAbsoluteTime(n.created_at)}>{formatRelativeTime(n.created_at)}</div>
                <div className="notif-payload">{n.data ? JSON.stringify(n.data) : JSON.stringify(n)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;